import { useEffect, JSX } from 'react'
import React from 'react'

import { Controller, FieldValues, useFormState } from 'react-hook-form'

import {
  DISABLED_INPUT_CLASSNAME,
  DisabledReasonTooltip,
  Group,
} from '~/components/component-library'
import { TextAreaInputProps } from '~/components/component-library/TextAreaInput/types'
import { ErrorFooter, visibleError } from '~/components/inputs/ErrorFooter'
import { cn } from '~/utils/tailwind-utils'

export const TextAreaInput = <FormType extends FieldValues = FieldValues>({
  id,
  control,
  inputName,
  placeholder,
  validationFunc = () => true,
  autoFocus,
  autoComplete = 'off',
  optional = false,
  requiredErrorMessage = 'Required',
  disabled = false,
  disabledReason,
  maxLength,
  rows = 3,
  className,
  label,
  description,
  footer,
  tooltip,
  onFocus,
  onBlur,
  onKeyDown,
  resizable = false,
  secondaryDisplay,
  size = 'base',
}: TextAreaInputProps<FormType>): JSX.Element => {
  const inputElementRef = React.useRef<HTMLTextAreaElement>(null)
  const { isSubmitted } = useFormState({ control })

  useEffect(() => {
    if (inputElementRef.current) {
      if (autoFocus) {
        // TODO: This is a hack to ensure the focus set by delaying the focus to the next tick
        // I believe the radix dropdown menu is causing the issue by somehow hijacking the focus
        setTimeout(() => {
          if (inputElementRef.current) {
            inputElementRef.current.focus()
            // Place cursor at the end of the text
            const length = inputElementRef.current.value.length
            inputElementRef.current.setSelectionRange(length, length)
          }
        }, 0)
      }
    }
  }, [autoFocus])

  return (
    <Controller
      control={control}
      name={inputName}
      render={({ field, fieldState }) => {
        const message = visibleError(fieldState.error, {
          isTouched: fieldState.isTouched,
          isEmpty: !field.value,
          isSubmitted,
        })
        return (
          <Group
            label={label}
            secondaryLabel={optional ? 'Optional' : undefined}
            secondaryDisplay={secondaryDisplay}
            footer={message ? <ErrorFooter message={message} /> : footer}
            description={description}
            tooltip={tooltip}
          >
            <DisabledReasonTooltip
              disabled={disabled}
              disabledReason={disabledReason}
              className="block w-full"
            >
              <textarea
                ref={inputElementRef}
                id={id}
                tabIndex={0}
                rows={rows}
                className={cn(
                  'w-full rounded-lg text-primary outline-none ring-1 ring-inset ring-default transition placeholder:text-placeholder focus:ring-2 focus:ring-focus',
                  {
                    'px-3 py-1.5 text-sm': size === 'small',
                    'px-3.5 py-2.5': size === 'base',
                  },
                  !resizable && 'resize-none',
                  message && 'text-error ring-error focus:ring-error',
                  'bg-surface-raised',
                  disabled && DISABLED_INPUT_CLASSNAME,
                  className,
                  { 'min-h-[140px]': resizable },
                )}
                placeholder={placeholder}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                name={inputName}
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={() => {
                  onBlur?.()
                  field.onBlur()
                }}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                disabled={disabled}
                maxLength={maxLength}
              />
            </DisabledReasonTooltip>
          </Group>
        )
      }}
      rules={{
        required: {
          value: !optional,
          message: requiredErrorMessage,
        },
        validate: validationFunc,
      }}
    />
  )
}

TextAreaInput.displayName = 'TextAreaInput'

export type { TextAreaInputProps } from '~/components/component-library/TextAreaInput/types'
